import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  LockKeyhole,
  LogOut,
  MapPin,
  PackageCheck,
  RefreshCw,
  Route,
  ShieldCheck,
} from 'lucide-react'
import { useParams } from 'react-router-dom'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api/client'
import type {
  PublicTrackingContainer,
  PublicTrackingDocument,
  PublicTrackingPosition,
  PublicTrackingSummary,
  PublicTrackingTimelineEvent,
} from '@/lib/api/types'
import { PublicRouteMap } from '@/src/components/tracking/PublicRouteMap'
import { XtellarMark } from '@/src/components/XtellarMark'
import {
  clearConsumedPublicTrackingFragment,
  clearPublicTrackingSessionConfig,
  consumePublicTrackingFragment,
  loadPublicTrackingSessionConfig,
  savePublicTrackingSessionConfig,
  type PublicTrackingSessionConfig,
} from '@/src/lib/public-tracking'

type PortalState = 'loading' | 'passcode' | 'ready' | 'unavailable' | 'signed-out'

const unavailableMessage =
  'This tracking link is unavailable. It may have expired, been revoked, or reached its access limit.'

function formatDate(value: string | null | undefined, includeTime = true) {
  if (!value) return 'Not available'
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' as const } : {}),
  }).format(new Date(value))
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not available'
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function PublicPortalHeader({ onLogout }: { onLogout?: () => void }) {
  return (
    <header className="border-b bg-background">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2" aria-label="Helm public tracking">
          <XtellarMark />
          <span className="font-semibold">Helm Tracking</span>
        </div>
        {onLogout && (
          <Button type="button" size="sm" variant="ghost" onClick={onLogout}>
            <LogOut className="size-4" aria-hidden="true" />
            Close session
          </Button>
        )}
      </div>
    </header>
  )
}

function LoadingPortal() {
  return (
    <>
      <PublicPortalHeader />
      <main className="mx-auto max-w-6xl space-y-5 px-4 py-8 sm:px-6">
        <div className="space-y-3" role="status" aria-label="Opening secure tracking">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-9 w-full max-w-lg" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-24 rounded-lg" />
        </div>
        <Skeleton className="h-56 rounded-lg" />
        <span className="sr-only">Opening secure tracking</span>
      </main>
    </>
  )
}

function ClosedPortal({
  state,
  onRetry,
}: {
  state: 'unavailable' | 'signed-out'
  onRetry: () => void
}) {
  return (
    <>
      <PublicPortalHeader />
      <main className="mx-auto flex min-h-[calc(100vh-3.5rem)] max-w-lg items-center px-4 py-10 sm:px-6">
        <section className="w-full rounded-lg border bg-card p-6 text-center sm:p-8">
          <div className="mx-auto mb-4 flex size-10 items-center justify-center rounded-full bg-muted">
            {state === 'signed-out' ? (
              <CheckCircle2 className="size-5" aria-hidden="true" />
            ) : (
              <LockKeyhole className="size-5" aria-hidden="true" />
            )}
          </div>
          <h1 className="text-xl font-semibold">
            {state === 'signed-out' ? 'Tracking session closed' : 'Tracking unavailable'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {state === 'signed-out'
              ? 'Your public tracking session has been closed on this device.'
              : unavailableMessage}
          </p>
          {state === 'unavailable' && (
            <Button type="button" variant="outline" className="mt-5" onClick={onRetry}>
              <RefreshCw className="size-4" aria-hidden="true" />
              Try again
            </Button>
          )}
        </section>
      </main>
    </>
  )
}

function PasscodePortal({ onUnlock }: { onUnlock: (passcode: string) => Promise<void> }) {
  const [passcode, setPasscode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onUnlock(passcode)
    } catch {
      setError('The passcode could not be accepted. Check it and try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PublicPortalHeader />
      <main className="mx-auto flex min-h-[calc(100vh-3.5rem)] max-w-lg items-center px-4 py-10 sm:px-6">
        <section className="w-full rounded-lg border bg-card p-6 sm:p-8">
          <div className="mb-5 flex size-10 items-center justify-center rounded-full bg-muted">
            <LockKeyhole className="size-5" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold">Enter tracking passcode</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This tracking link requires the passcode supplied by the sender.
          </p>
          {error && (
            <Alert variant="destructive" className="mt-5">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form className="mt-5 space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="public-tracking-passcode">Passcode</Label>
              <Input
                id="public-tracking-passcode"
                type="password"
                autoComplete="current-password"
                minLength={6}
                required
                autoFocus
                value={passcode}
                onChange={(event) => setPasscode(event.target.value)}
              />
            </div>
            <Button type="submit" variant="brand" className="w-full" disabled={busy}>
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ShieldCheck className="size-4" />
              )}
              Unlock tracking
            </Button>
          </form>
        </section>
      </main>
    </>
  )
}

function TrackingPortal({
  summary,
  timeline,
  documents,
  containers,
  position,
  config,
  onLogout,
}: {
  summary: PublicTrackingSummary
  timeline: PublicTrackingTimelineEvent[]
  documents: PublicTrackingDocument[]
  containers: PublicTrackingContainer[]
  position: PublicTrackingPosition | null
  config: PublicTrackingSessionConfig
  onLogout: () => void
}) {
  const showTimeline = config.sections.includes('timeline')
  const showDocuments = config.sections.includes('documents')
  const showContainers = config.sections.includes('containers')
  const showPosition = config.sections.includes('live_position')
  const origin = summary.origin_country || 'Origin pending'
  const destination = summary.destination_address || 'Destination pending'

  return (
    <>
      <PublicPortalHeader onLogout={onLogout} />
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex flex-col gap-4 border-b pb-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
              <PackageCheck className="size-4" aria-hidden="true" />
              <span>{summary.code}</span>
            </div>
            <h1 className="break-words text-2xl font-semibold sm:text-3xl">{summary.title}</h1>
            {summary.description && (
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{summary.description}</p>
            )}
          </div>
          <StatusBadge status={summary.status} className="w-fit" />
        </header>

        <dl className="grid overflow-hidden rounded-lg border sm:grid-cols-3">
          <div className="border-b p-4 sm:border-b-0 sm:border-r">
            <dt className="text-xs font-medium uppercase text-muted-foreground">Current status</dt>
            <dd className="mt-1 font-medium">
              {formatLabel(summary.operational_state || summary.status)}
            </dd>
          </div>
          <div className="border-b p-4 sm:border-b-0 sm:border-r">
            <dt className="text-xs font-medium uppercase text-muted-foreground">
              Tracking reference
            </dt>
            <dd className="mt-1 break-words font-medium">
              {summary.international_tracking_number || summary.code}
            </dd>
          </div>
          <div className="p-4">
            <dt className="text-xs font-medium uppercase text-muted-foreground">Last updated</dt>
            <dd className="mt-1 font-medium">{formatDate(summary.updated_at)}</dd>
          </div>
        </dl>

        <section aria-labelledby="public-route-title">
          <div className="mb-3 flex items-center gap-2">
            <Route className="size-4" aria-hidden="true" />
            <h2 id="public-route-title" className="font-semibold">
              Route
            </h2>
          </div>
          <div className="rounded-lg border p-4 sm:p-5">
            <div className="grid items-start gap-4 sm:grid-cols-[1fr_auto_1fr]">
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Origin</p>
                <p className="mt-1 font-medium">{origin}</p>
              </div>
              <div
                className="hidden items-center gap-2 pt-4 text-muted-foreground sm:flex"
                aria-hidden="true"
              >
                <span className="h-px w-12 bg-border" />
                <ArrowRight className="size-4" />
                <span className="h-px w-12 bg-border" />
              </div>
              <div className="sm:text-right">
                <p className="text-xs font-medium uppercase text-muted-foreground">Destination</p>
                <p className="mt-1 font-medium">{destination}</p>
              </div>
            </div>
          </div>
        </section>

        {showPosition && <PublicRouteMap position={position} containers={containers} />}

        {showContainers && containers.length > 0 && (
          <section className="space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-medium">
              <PackageCheck className="size-4 text-muted-foreground" />
              Containers
              <span className="text-xs font-normal text-muted-foreground">{containers.length}</span>
            </h2>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[32rem] text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Container</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {containers.map((container) => (
                    <tr key={container.container_number} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium tabular-nums">
                        {container.container_number ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {[container.container_size, container.container_type]
                          .filter(Boolean)
                          .join("' ") || '—'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {formatLabel(container.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {showTimeline && (
          <section aria-labelledby="public-timeline-title">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CalendarClock className="size-4" aria-hidden="true" />
                <h2 id="public-timeline-title" className="font-semibold">
                  Tracking timeline
                </h2>
              </div>
              <span className="text-xs text-muted-foreground">{timeline.length} updates</span>
            </div>
            <ol className="rounded-lg border">
              {timeline.length === 0 ? (
                <li className="p-4 text-sm text-muted-foreground">
                  No tracking updates are available yet.
                </li>
              ) : (
                timeline.map((event, index) => (
                  <li key={event.id} className="grid gap-2 p-4 sm:grid-cols-[10rem_1fr]">
                    <time className="text-sm text-muted-foreground" dateTime={event.occurred_at}>
                      {formatDate(event.occurred_at)}
                    </time>
                    <div className="min-w-0">
                      <p className="font-medium">{formatLabel(event.event_type)}</p>
                      {event.location && (
                        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
                          {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p className="mt-1 text-sm text-muted-foreground">{event.description}</p>
                      )}
                    </div>
                    {index < timeline.length - 1 && <Separator className="sm:col-span-2" />}
                  </li>
                ))
              )}
            </ol>
          </section>
        )}

        {showDocuments && (
          <section aria-labelledby="public-documents-title">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="size-4" aria-hidden="true" />
              <h2 id="public-documents-title" className="font-semibold">
                Shared documents
              </h2>
            </div>
            <div className="overflow-hidden rounded-lg border">
              {documents.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  No documents were shared with this link.
                </p>
              ) : (
                <ul className="divide-y">
                  {documents.map((document) => (
                    <li key={document.id} className="flex items-center justify-between gap-4 p-4">
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {document.file_name || formatLabel(document.type)}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatLabel(document.type)} · Version {document.version}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(document.created_at, false)}
                        </span>
                        <a
                          href={apiClient.publicTrackingDocumentUrl(document.id)}
                          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                          aria-label={`Download ${document.file_name || formatLabel(document.type)}`}
                        >
                          <Download className="size-3.5" />
                          Download
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        <footer className="border-t pt-4 text-xs text-muted-foreground">
          This restricted view shows only the tracking information approved for this link.
        </footer>
      </main>
    </>
  )
}

export function PublicTrackingPage() {
  const { publicId = '' } = useParams()
  const started = useRef(false)
  const [state, setState] = useState<PortalState>('loading')
  const [summary, setSummary] = useState<PublicTrackingSummary | null>(null)
  const [timeline, setTimeline] = useState<PublicTrackingTimelineEvent[]>([])
  const [documents, setDocuments] = useState<PublicTrackingDocument[]>([])
  const [containers, setContainers] = useState<PublicTrackingContainer[]>([])
  const [position, setPosition] = useState<PublicTrackingPosition | null>(null)
  const [config, setConfig] = useState<PublicTrackingSessionConfig>(() =>
    loadPublicTrackingSessionConfig(publicId),
  )

  useEffect(() => {
    const previousTitle = document.title
    const existingRobotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
    const previousRobotsPolicy = existingRobotsMeta?.content
    const robotsMeta = existingRobotsMeta ?? document.createElement('meta')
    robotsMeta.name = 'robots'
    robotsMeta.content = 'noindex, nofollow, noarchive, nosnippet'
    if (!existingRobotsMeta) document.head.append(robotsMeta)

    const existingReferrerMeta = document.querySelector<HTMLMetaElement>('meta[name="referrer"]')
    const previousReferrerPolicy = existingReferrerMeta?.content
    const referrerMeta = existingReferrerMeta ?? document.createElement('meta')
    referrerMeta.name = 'referrer'
    referrerMeta.content = 'no-referrer'
    if (!existingReferrerMeta) document.head.append(referrerMeta)

    return () => {
      document.title = previousTitle
      if (existingReferrerMeta) referrerMeta.content = previousReferrerPolicy ?? ''
      else referrerMeta.remove()
      if (existingRobotsMeta) robotsMeta.content = previousRobotsPolicy ?? ''
      else robotsMeta.remove()
    }
  }, [])

  const loadTracking = useCallback(async (nextConfig: PublicTrackingSessionConfig) => {
    const nextSummary = await apiClient.getPublicTrackingSummary()
    const nextTimeline = nextConfig.sections.includes('timeline')
      ? await apiClient.getPublicTrackingTimeline()
      : []
    // Each section is optional and independent, so one being switched off must
    // not stop the others loading.
    const nextContainers = nextConfig.sections.includes('containers')
      ? await apiClient.getPublicTrackingContainers().catch(() => [])
      : []
    const nextPosition = nextConfig.sections.includes('live_position')
      ? await apiClient.getPublicTrackingPosition().catch(() => null)
      : null
    const nextDocuments = nextConfig.sections.includes('documents')
      ? (
          await Promise.allSettled(
            nextConfig.documentIds.map((documentId) =>
              apiClient.getPublicTrackingDocument(documentId),
            ),
          )
        )
          .filter(
            (result): result is PromiseFulfilledResult<PublicTrackingDocument> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value)
      : []
    setSummary(nextSummary)
    setTimeline(nextTimeline)
    setDocuments(nextDocuments)
    setContainers(nextContainers)
    setPosition(nextPosition)
    document.title = `${nextSummary.code} | Helm Tracking`
    setState('ready')
  }, [])

  const begin = useCallback(async () => {
    setState('loading')
    setSummary(null)
    setTimeline([])
    setDocuments([])
    setContainers([])
    setPosition(null)
    try {
      const fragment = consumePublicTrackingFragment(publicId)
      const nextConfig = fragment.sections.length
        ? { documentIds: fragment.documentIds, sections: fragment.sections }
        : loadPublicTrackingSessionConfig(publicId)
      setConfig(nextConfig)

      if (fragment.token) {
        const exchange = await apiClient.exchangePublicTrackingToken(publicId, fragment.token)
        savePublicTrackingSessionConfig(publicId, nextConfig)
        clearConsumedPublicTrackingFragment(publicId)
        if (!exchange.unlocked) {
          setState('passcode')
          return
        }
      }
      await loadTracking(nextConfig)
    } catch {
      setState('unavailable')
    }
  }, [loadTracking, publicId])

  useEffect(() => {
    if (started.current) return
    started.current = true
    void begin()
  }, [begin])

  async function unlock(passcode: string) {
    await apiClient.unlockPublicTracking(publicId, passcode)
    await loadTracking(config)
  }

  async function logout() {
    try {
      await apiClient.closePublicTrackingSession()
    } finally {
      clearConsumedPublicTrackingFragment(publicId)
      clearPublicTrackingSessionConfig(publicId)
      setSummary(null)
      setTimeline([])
      setDocuments([])
      document.title = 'Helm Tracking'
      setState('signed-out')
    }
  }

  if (state === 'loading') return <LoadingPortal />
  if (state === 'passcode') return <PasscodePortal onUnlock={unlock} />
  if (state === 'unavailable' || state === 'signed-out') {
    return (
      <ClosedPortal
        state={state}
        onRetry={() => {
          started.current = false
          void begin()
        }}
      />
    )
  }
  if (!summary) return <LoadingPortal />
  return (
    <TrackingPortal
      summary={summary}
      timeline={timeline}
      documents={documents}
      containers={containers}
      position={position}
      config={config}
      onLogout={() => void logout()}
    />
  )
}
